import React, { useState } from "react";
import CookieConsent, { getCookieConsentValue } from "react-cookie-consent";
import Cookies from "js-cookie";
import {
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Collapse,
  Link,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const COOKIE_NAME = "userConsent";
const CONSENT_DETAIL_COOKIE = "cookiePreferences";

const defaultPreferences = {
  necessary: true, // always on
  analytics: false,
  marketing: false,
};

function getStoredPreferences() {
  try {
    const raw = Cookies.get(CONSENT_DETAIL_COOKIE);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return null;
}

function savePreferences(prefs) {
  Cookies.set(CONSENT_DETAIL_COOKIE, JSON.stringify(prefs), { expires: 365 });
  // Set individual cookies for backward compatibility
  Cookies.set("analytics", prefs.analytics ? "enabled" : "disabled", { expires: 365 });
  Cookies.set("marketing", prefs.marketing ? "enabled" : "disabled", { expires: 365 });
}

const CookieConsentBanner = () => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState(defaultPreferences);

  const handleAcceptAll = () => {
    const prefs = { necessary: true, analytics: true, marketing: true };
    savePreferences(prefs);
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
    // Programmatically set the consent cookie so the banner hides
    Cookies.set(COOKIE_NAME, "true", { expires: 365 });
    window.location.reload();
  };

  const handleDecline = () => {
    const prefs = { necessary: true, analytics: false, marketing: false };
    savePreferences(prefs);
    Cookies.remove("analytics");
    Cookies.remove("marketing");
  };

  const togglePref = (key) => {
    if (key === "necessary") return; // cannot disable
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // If consent was already given, don't render
  const existing = getCookieConsentValue(COOKIE_NAME);
  if (existing === "true") return null;

  return (
    <CookieConsent
      location="bottom"
      cookieName={COOKIE_NAME}
      enableDeclineButton
      buttonText={t("accept")}
      declineButtonText={t("decline")}
      style={{
        backgroundColor: "#1a1f2e",
        padding: "12px 20px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        flexWrap: "wrap",
        gap: "8px",
        zIndex: 9999,
        borderTop: "1px solid rgba(255,255,255,0.1)",
      }}
      buttonStyle={{
        backgroundColor: "#4caf50",
        color: "#fff",
        fontSize: "14px",
        borderRadius: "4px",
        padding: "6px 16px",
      }}
      declineButtonStyle={{
        backgroundColor: "transparent",
        border: "1px solid #888",
        color: "#ccc",
        fontSize: "14px",
        borderRadius: "4px",
        padding: "6px 16px",
      }}
      ButtonComponent={(props) => (
        <Button
          {...props}
          variant="contained"
          size="small"
          style={{ ...props.style, backgroundColor: props.style?.backgroundColor }}
        >
          {props.children}
        </Button>
      )}
      onAccept={handleAcceptAll}
      onDecline={handleDecline}
    >
      <Box sx={{ color: "#FFF", maxWidth: 800 }}>
        <Typography variant="body2" sx={{ fontSize: "14px", mb: 0.5 }}>
          {t("cookieMessage")}
        </Typography>

        <Link
          component="button"
          variant="body2"
          onClick={() => setShowDetails(!showDetails)}
          sx={{ color: "#90caf9", cursor: "pointer", fontSize: "13px" }}
        >
          {showDetails ? t("hideCookieSettings") || "Skjul innstillinger" : t("cookieSettings") || "Tilpass innstillinger"}
        </Link>

        <Collapse in={showDetails}>
          <Box
            sx={{
              mt: 1.5,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: "rgba(255,255,255,0.05)",
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            <FormControlLabel
              control={<Switch checked size="small" disabled sx={{ "& .Mui-disabled": { color: "#4caf50 !important" } }} />}
              label={
                <Typography variant="body2" sx={{ color: "#ccc", fontSize: "13px" }}>
                  <strong>{t("necessaryCookies") || "Nødvendige"}</strong> — {t("necessaryCookiesDesc") || "Påkrevd for at nettsiden skal fungere"}
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.analytics}
                  onChange={() => togglePref("analytics")}
                  size="small"
                  color="success"
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#ccc", fontSize: "13px" }}>
                  <strong>{t("analyticsCookies") || "Analyse"}</strong> — {t("analyticsCookiesDesc") || "Hjelper oss å forstå hvordan du bruker nettsiden"}
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.marketing}
                  onChange={() => togglePref("marketing")}
                  size="small"
                  color="success"
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#ccc", fontSize: "13px" }}>
                  <strong>{t("marketingCookies") || "Markedsføring"}</strong> — {t("marketingCookiesDesc") || "Brukes til å vise relevante annonser"}
                </Typography>
              }
            />
            <Box sx={{ mt: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleSavePreferences}
                sx={{ backgroundColor: "#1976d2", textTransform: "none" }}
              >
                {t("savePreferences") || "Lagre valg"}
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </CookieConsent>
  );
};

export default CookieConsentBanner;
